import { useCallback, useEffect, useRef, useState } from 'react';
import { apiGet } from '../api/client';

/**
 * Loading / loaded / failed, for one GET.
 *
 * This is the whole of the application's state management. A data-fetching
 * library or a store would both be more infrastructure than two read-only
 * pages need, and the project's own architectural instinct throughout has
 * been to add the smallest thing that works (plain `pg` over an ORM,
 * Express over a framework, node:test over a test framework). When a later
 * mission adds caching or mutations, replacing this hook is a contained
 * change — every component consumes the same three-state shape.
 */

export type ResourceState<T> =
  | { status: 'loading' }
  | { status: 'loaded'; data: T }
  | { status: 'failed'; error: unknown };

export function useApiResource<T>(path: string | null): ResourceState<T> & { reload: () => void } {
  const [state, setState] = useState<ResourceState<T>>({ status: 'loading' });
  /**
   * Bumped to re-run the effect against the same path (Mission 014).
   *
   * The write path needs this: after an assessment is recorded, the
   * composite, confidence, variance and trajectory on screen are all stale,
   * and every one of them is a domain derivation the browser must not
   * attempt to patch locally. Re-fetching is the only honest way to find out
   * what the new record meant, so the hook that owns the fetch is where the
   * ability to repeat it belongs.
   */
  const [reloadCount, setReloadCount] = useState(0);
  const reload = useCallback(() => setReloadCount((n) => n + 1), []);

  /** The path the currently-displayed data belongs to. */
  const loadedPath = useRef<string | null>(null);

  useEffect(() => {
    if (path === null) return;

    const controller = new AbortController();
    let active = true;

    /**
     * A *reload* keeps what is on screen; a *different resource* does not.
     *
     * Resetting to `loading` on every fetch looks harmless until something
     * re-reads while the reader is mid-task: the section unmounts, and
     * anything inside it — an open form, a confirmation just shown, a
     * scroll position — goes with it. The data on screen during a reload is
     * stale but real, which is a better thing to show than nothing. A
     * genuinely different path is a different resource, and there the old
     * data would be actively misleading, so it does blank.
     */
    if (loadedPath.current !== path) {
      loadedPath.current = path;
      setState({ status: 'loading' });
    }

    apiGet<T>(path, controller.signal)
      .then((data) => {
        if (active) setState({ status: 'loaded', data });
      })
      .catch((error: unknown) => {
        // An abort is this component unmounting or the path changing, not a
        // failure the user should ever be told about.
        if (active && !(error instanceof DOMException && error.name === 'AbortError')) {
          setState({ status: 'failed', error });
        }
      });

    return () => {
      active = false;
      controller.abort();
    };
  }, [path, reloadCount]);

  return { ...state, reload };
}
